/* @pareto-engineering/generator-front 1.0.12 */
import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styleNames from '@pareto-engineering/bem/exports';
import { Card } from "../../../..";

// Local Definitions

import useFirstVisibleNode from "../useFirstVisibleNode";
const baseClassName = styleNames.base;
const componentClassName = 'tree';

/**
 * This is the component description.
 */
const Tree = ({
  id,
  className: userClassName,
  style,
  tree,
  displayDepth,
  color
  // ...otherProps
}) => {
  // The nodeIds to be used to get the first visible node
  const [nodeIds, setNodeIds] = useState([]);

  // current visible nodeId
  const visibleNodeId = useFirstVisibleNode(nodeIds);
  useEffect(() => {
    const node = document.getElementsByClassName(visibleNodeId)[0];
    node?.classList.add(styleNames.modifierActive);
    return () => {
      node?.classList.remove(styleNames.modifierActive);
    };
  }, [visibleNodeId]);

  // Generate the tree structure from the content tree data depending on the display depth
  const getNestedTree = (node, depth) => {
    setNodeIds(prev => [...prev, node.id]);
    if (depth <= 1) {
      return /*#__PURE__*/React.createElement("li", {
        key: node.id
      }, /*#__PURE__*/React.createElement("a", {
        className: node.id,
        href: node.id
      }, node.text));
    }
    return /*#__PURE__*/React.createElement("li", {
      key: node.id
    }, /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("a", {
      href: node.id,
      className: node.id
    }, node.text)), node.children.length > 0 && /*#__PURE__*/React.createElement("ul", null, node.children.map(child => getNestedTree(child, depth - 1))));
  };
  const ContentTree = useMemo(() => tree.map(node => getNestedTree(node, displayDepth)), [tree]);
  return ContentTree.length ? /*#__PURE__*/React.createElement(Card, {
    id: id,
    className: [baseClassName, componentClassName, userClassName, color && `x-${color}`].filter(e => e).join(' '),
    style: style
  }, /*#__PURE__*/React.createElement(Card.Section, null, /*#__PURE__*/React.createElement("ul", null, ContentTree))) : null;
};
Tree.propTypes = {
  /**
   * The HTML id for this element
   */
  id: PropTypes.string,
  /**
   * The HTML class names for this element
   */
  className: PropTypes.string,
  /**
   * The React-written, css properties for this element.
   */
  style: PropTypes.objectOf(PropTypes.string),
  /**
   * The tree to render.
   */
  tree: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    id: PropTypes.string,
    children: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string,
      id: PropTypes.string
    }))
  })),
  /**
   * The levels of the tree to display.
   */
  displayDepth: PropTypes.number,
  /**
   * The color of the tree.
   */
  color: PropTypes.string
};
Tree.defaultProps = {
  color: 'interactive',
  displayDepth: 4
};
export default Tree;